import type { flightEntity } from 'freedom-shared';

import { getCurrentFlightTimeAndDate } from '../lib';
import type { DirectFlightGroup, StoredTimes } from '../types';

/**
 * Compares two offers by price.
 * @returns Negative if a is cheaper, positive if b is cheaper, 0 if equal
 */
function compareByPrice(
  a: flightEntity.types.MappedOffer,
  b: flightEntity.types.MappedOffer,
): number {
  return Number(a.price.amount) - Number(b.price.amount);
}

/**
 * Compares two offers by time for a specific flight index (earliest first).
 * @returns Negative if a is earlier, positive if b is earlier, 0 if equal
 */
function compareByTime(
  a: flightEntity.types.MappedOffer,
  b: flightEntity.types.MappedOffer,
  flightIndex: number,
): number {
  const { time: timeA } = getCurrentFlightTimeAndDate(a, flightIndex);
  const { time: timeB } = getCurrentFlightTimeAndDate(b, flightIndex);
  return timeA.localeCompare(timeB);
}

/**
 * Sorts offers by price only.
 * Returns a new sorted array.
 * @param offers - Offers to sort
 */
export function sortOffersByPrice(
  offers: flightEntity.types.MappedOffer[],
): flightEntity.types.MappedOffer[] {
  return [...offers].sort(compareByPrice);
}

/**
 * Sorts offers by price first, then by time (earliest first) when prices are equal.
 * Returns a new sorted array.
 * @param offers - Offers to sort
 * @param flightIndex - Flight index to use for time comparison (0 for forward, 1 for backward)
 */
export function sortOffersByPriceAndTime(
  offers: flightEntity.types.MappedOffer[],
  flightIndex: number,
): flightEntity.types.MappedOffer[] {
  return [...offers].sort((a, b) => {
    const priceCompare = compareByPrice(a, b);
    if (priceCompare !== 0) {
      return priceCompare;
    }

    // If prices are equal, compare by time (earliest first)
    return compareByTime(a, b, flightIndex);
  });
}

/**
 * Sorts RT offers by price, then forward time, then backward time (earliest first).
 * Returns a new sorted array.
 * @param offers - Offers to sort (must be RT offers)
 */
function sortOffersByPriceAndBothTimes(
  offers: flightEntity.types.MappedOffer[],
): flightEntity.types.MappedOffer[] {
  return [...offers].sort((a, b) => {
    const priceCompare = compareByPrice(a, b);
    if (priceCompare !== 0) {
      return priceCompare;
    }

    // If prices are equal, compare by forward time (earliest first)
    const forwardTimeCompare = compareByTime(a, b, 0);
    if (forwardTimeCompare !== 0) {
      return forwardTimeCompare;
    }

    // If forward times are also equal, compare by backward time (earliest first)
    return compareByTime(a, b, 1);
  });
}

export function findCheapestOfferFromGroup(
  group: DirectFlightGroup,
  isRoundTrip: boolean,
): flightEntity.types.MappedOffer {
  if (isRoundTrip) {
    // For RT: Sort by price, then forward time, then backward time
    const sortedOffers = sortOffersByPriceAndBothTimes(group.offers);
    return sortedOffers[0];
  }

  // For OW: Sort by price and forward time
  const sortedOffers = sortOffersByPriceAndTime(group.offers, 0);
  return sortedOffers[0];
}

/**
 * Finds an offer from a group matching stored times.
 * Tries to find exact match first, then falls back to partial matches or cheapest.
 *
 * @param group - The direct flight group to search in
 * @param storedTimes - The stored times to match (OW or RT format)
 * @param isRoundTrip - Whether this is a round trip flight
 * @returns The best matching offer, or null if group is empty
 */
export function findOfferByStoredTimes(
  group: DirectFlightGroup,
  storedTimes: StoredTimes | undefined,
  isRoundTrip: boolean,
): flightEntity.types.MappedOffer | null {
  if (group.offers.length === 0) {
    return null;
  }

  // If no stored times, return cheapest/earliest
  if (!storedTimes) {
    return findCheapestOfferFromGroup(group, isRoundTrip);
  }

  if (isRoundTrip && 'forwardTime' in storedTimes) {
    // For RT: Try to find offer with both forward and backward times
    const exactMatch = group.offers.find(offer => {
      const { time: fTime, date: fDate } = getCurrentFlightTimeAndDate(
        offer,
        0,
      );
      const { time: bTime, date: bDate } = getCurrentFlightTimeAndDate(
        offer,
        1,
      );
      return (
        fTime === storedTimes.forwardTime &&
        fDate === storedTimes.forwardDate &&
        bTime === storedTimes.backwardTime &&
        bDate === storedTimes.backwardDate
      );
    });

    if (exactMatch) {
      return exactMatch;
    }

    // If not found, try to find with just forward time
    const forwardMatches = group.offers.filter(offer => {
      const { time: fTime, date: fDate } = getCurrentFlightTimeAndDate(
        offer,
        0,
      );
      return (
        fTime === storedTimes.forwardTime && fDate === storedTimes.forwardDate
      );
    });

    if (forwardMatches.length > 0) {
      // Since all offers have the same forward time, sort by price then backward time (earliest first)
      const sorted = sortOffersByPriceAndTime(forwardMatches, 1);
      return sorted[0];
    }
  } else if (!isRoundTrip && 'time' in storedTimes) {
    // For OW: Try to find offer with stored time
    const matches = group.offers.filter(offer => {
      const { time, date } = getCurrentFlightTimeAndDate(offer, 0);
      return time === storedTimes.time && date === storedTimes.date;
    });

    if (matches.length > 0) {
      // All matching offers have the same forward time, so just sort by price
      const sorted = sortOffersByPrice(matches);
      return sorted[0];
    }
  }

  // Fall back to cheapest/earliest if no match found
  return findCheapestOfferFromGroup(group, isRoundTrip);
}
